import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Chip, Tabs, Tab, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, LinearProgress, CircularProgress,
  Autocomplete, InputAdornment, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Edit, Add, Delete, KeyboardArrowUp, KeyboardArrowDown,
  AttachMoney, FolderOpen,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Project, Task, ProjectStatus, Client } from '../../types';
import { useProjects } from '../context_providers/ProjectContext';
import { useNotification } from '../context_providers/NotificationContext';
import apiService from '../../utils/api.service';
import LoadingSpinner from '../shared/LoadingSpinner';

const PROJECT_COLORS = [
  '#7b6df6', '#10b981', '#3b82f6', '#f59e0b', '#ef4444',
  '#ec4899', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316',
];

const STATUS_COLORS: Record<ProjectStatus, 'success' | 'default' | 'info'> = {
  active: 'success', archived: 'default', completed: 'info',
};

interface BudgetInfo {
  budgetHours: number;
  usedHours: number;
  budgetAmount: number;
  usedAmount: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { clients, updateProject, deleteProject } = useProjects();
  const { showSuccess, showError } = useNotification();

  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [budget, setBudget] = useState<BudgetInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  // Edit project dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Project>>({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [taskForm, setTaskForm] = useState({ name: '', isBillable: true });
  const [taskSubmitting, setTaskSubmitting] = useState(false);

  // Delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);

  const clientMap = React.useMemo(() => {
    const map: Record<string, Client> = {};
    clients.forEach(c => { map[c.id] = c; });
    return map;
  }, [clients]);

  const fetchProject = useCallback(async () => {
    if (!id) return;
    try {
      const response = await apiService.getProject(id);
      const projData = response.data || response;
      setProject(projData?.project || projData);
    } catch (err: any) {
      showError(err.message || 'Failed to load project');
    }
  }, [id, showError]);

  const fetchTasks = useCallback(async () => {
    if (!id) return;
    try {
      const response = await apiService.listProjectTasks(id);
      const taskList = response.data || response;
      const tasks = Array.isArray(taskList) ? taskList : (taskList?.tasks || []);
      setTasks(tasks.sort((a: Task, b: Task) => a.sortOrder - b.sortOrder));
    } catch {
      // Tasks endpoint may not exist yet - fail silently
      setTasks([]);
    }
  }, [id]);

  const fetchBudget = useCallback(async () => {
    if (!id) return;
    try {
      const response = await apiService.getProjectBudget(id);
      const budgetData = response.data || response;
      setBudget(budgetData?.budget || budgetData);
    } catch {
      setBudget(null);
    }
  }, [id]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchProject(), fetchTasks(), fetchBudget()])
      .finally(() => setIsLoading(false));
  }, [fetchProject, fetchTasks, fetchBudget]);

  const handleOpenEdit = () => {
    if (!project) return;
    setEditForm({
      name: project.name,
      code: project.code,
      clientId: project.clientId,
      description: project.description,
      color: project.color,
      isBillable: project.isBillable,
      hourlyRate: project.hourlyRate,
      budgetHours: project.budgetHours,
      status: project.status,
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!id || !editForm.name?.trim()) return;
    setEditSubmitting(true);
    try {
      await updateProject(id, editForm);
      await fetchProject();
      showSuccess('Project updated');
      setEditOpen(false);
    } catch (err: any) {
      showError(err.message || 'Failed to update project');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!id) return;
    try {
      await deleteProject(id);
      showSuccess('Project deleted');
      navigate('/projects');
    } catch (err: any) {
      showError(err.message || 'Failed to delete project');
    }
  };

  const handleOpenTaskDialog = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setTaskForm({ name: task.name, isBillable: task.isBillable });
    } else {
      setEditingTask(null);
      setTaskForm({ name: '', isBillable: true });
    }
    setTaskDialogOpen(true);
  };

  const handleTaskSubmit = async () => {
    if (!id || !taskForm.name.trim()) return;
    setTaskSubmitting(true);
    try {
      if (editingTask) {
        await apiService.updateProjectTask(id, editingTask.id, taskForm);
        showSuccess('Task updated');
      } else {
        await apiService.createProjectTask(id, {
          ...taskForm,
          sortOrder: tasks.length,
        });
        showSuccess('Task created');
      }
      await fetchTasks();
      setTaskDialogOpen(false);
    } catch (err: any) {
      showError(err.message || 'Failed to save task');
    } finally {
      setTaskSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!id) return;
    try {
      await apiService.deleteProjectTask(id, taskId);
      showSuccess('Task deleted');
      await fetchTasks();
    } catch (err: any) {
      showError(err.message || 'Failed to delete task');
    }
  };

  const handleMoveTask = async (taskId: string, direction: 'up' | 'down') => {
    if (!id) return;
    const idx = tasks.findIndex(t => t.id === taskId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= tasks.length) return;

    try {
      await apiService.updateProjectTask(id, tasks[idx].id, { sortOrder: tasks[swapIdx].sortOrder });
      await apiService.updateProjectTask(id, tasks[swapIdx].id, { sortOrder: tasks[idx].sortOrder });
      await fetchTasks();
    } catch (err: any) {
      showError(err.message || 'Failed to reorder tasks');
    }
  };

  const handleToggleTaskActive = async (task: Task) => {
    if (!id) return;
    try {
      await apiService.updateProjectTask(id, task.id, { isActive: !task.isActive });
      await fetchTasks();
    } catch (err: any) {
      showError(err.message || 'Failed to toggle task');
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading project..." />;
  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <FolderOpen sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">Project not found</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/projects')}>Back to Projects</Button>
      </Container>
    );
  }

  const client = project.clientId ? clientMap[project.clientId] : null;
  const budgetHours = project.budgetHours ? parseFloat(project.budgetHours) : 0;
  const usedHours = budget?.usedHours || 0;
  const budgetPct = budgetHours > 0 ? Math.min((usedHours / budgetHours) * 100, 100) : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back + Header */}
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/projects')} sx={{ mb: 2 }}>
        Back to Projects
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: project.color, flexShrink: 0 }} />
        <Typography variant="h4" fontWeight={700}>{project.name}</Typography>
        <Chip label={project.code} size="small" sx={{ fontWeight: 600, fontFamily: 'monospace' }} />
        <Chip label={project.status} size="small" color={STATUS_COLORS[project.status]} variant="outlined" />
        {project.isBillable && (
          <Chip icon={<AttachMoney />} label="Billable" size="small" color="success" variant="outlined" />
        )}
        <Box sx={{ ml: 'auto' }}>
          <Button startIcon={<Edit />} variant="outlined" size="small" onClick={handleOpenEdit}>
            Edit
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tab label="Overview" />
        <Tab label="Tasks" />
        <Tab label="Budget" />
      </Tabs>

      {/* Overview Tab */}
      <TabPanel value={tabIndex} index={0}>
        <Card sx={{ border: '1px solid rgba(148, 163, 184, 0.08)' }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {project.description && (
              <Box>
                <Typography variant="overline" color="text.secondary">Description</Typography>
                <Typography variant="body1">{project.description}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">Client</Typography>
                <Typography variant="body1">{client?.name || 'No client'}</Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Billable Rate</Typography>
                <Typography variant="body1">
                  {project.isBillable && project.hourlyRate ? `$${project.hourlyRate}/hr` : project.isBillable ? 'Billable (no rate set)' : 'Non-billable'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Created</Typography>
                <Typography variant="body1">{format(new Date(project.createdAt), 'MMMM d, yyyy')}</Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Updated</Typography>
                <Typography variant="body1">{format(new Date(project.updatedAt), 'MMMM d, yyyy')}</Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Assigned Members</Typography>
                <Typography variant="body1">
                  {project.assignedUserIds?.length || 0} member{(project.assignedUserIds?.length || 0) !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Budget</Typography>
                <Typography variant="body1">{budgetHours > 0 ? `${budgetHours} hours` : 'No budget set'}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tasks Tab */}
      <TabPanel value={tabIndex} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>Tasks ({tasks.length})</Typography>
          <Button startIcon={<Add />} variant="contained" size="small" onClick={() => handleOpenTaskDialog()}>
            Add Task
          </Button>
        </Box>
        {tasks.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6, border: '1px solid rgba(148, 163, 184, 0.08)' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>No tasks yet.</Typography>
            <Button startIcon={<Add />} variant="outlined" onClick={() => handleOpenTaskDialog()}>
              Add First Task
            </Button>
          </Card>
        ) : (
          <TableContainer component={Card} sx={{ border: '1px solid rgba(148, 163, 184, 0.08)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell align="center">Billable</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="center">Order</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task, idx) => (
                  <TableRow key={task.id} sx={{ opacity: task.isActive ? 1 : 0.5 }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{task.name}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={task.isBillable ? 'Yes' : 'No'} size="small"
                        color={task.isBillable ? 'success' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Switch size="small" checked={task.isActive} onChange={() => handleToggleTaskActive(task)} />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton size="small" disabled={idx === 0} onClick={() => handleMoveTask(task.id, 'up')}>
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={idx === tasks.length - 1} onClick={() => handleMoveTask(task.id, 'down')}>
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenTaskDialog(task)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteTask(task.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Budget Tab */}
      <TabPanel value={tabIndex} index={2}>
        {budgetHours <= 0 ? (
          <Card sx={{ textAlign: 'center', py: 6, border: '1px solid rgba(148, 163, 184, 0.08)' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>No budget set</Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
              Edit the project to configure a budget.
            </Typography>
            <Button variant="outlined" onClick={handleOpenEdit}>Set Budget</Button>
          </Card>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Card sx={{ border: '1px solid rgba(148, 163, 184, 0.08)' }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Hours Budget</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                      variant="determinate"
                      value={budgetPct}
                      size={100}
                      thickness={6}
                      sx={{ color: budgetPct > 90 ? '#ef4444' : budgetPct > 75 ? '#f59e0b' : '#10b981' }}
                    />
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, bottom: 0, right: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography variant="body2" fontWeight={700}>{budgetPct.toFixed(0)}%</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{usedHours.toFixed(1)}</Typography>
                    <Typography variant="body2" color="text.secondary">of {budgetHours} hours used</Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                      {Math.max(budgetHours - usedHours, 0).toFixed(1)} hours remaining
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            {project.budgetAmount && (
              <Card sx={{ border: '1px solid rgba(148, 163, 184, 0.08)' }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Amount Budget</Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h5" fontWeight={700}>
                      ${(budget?.usedAmount || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      of ${parseFloat(project.budgetAmount).toFixed(2)} used
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(project.budgetAmount) > 0
                        ? Math.min(((budget?.usedAmount || 0) / parseFloat(project.budgetAmount)) * 100, 100)
                        : 0}
                      sx={{ mt: 2, height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </TabPanel>

      {/* Edit Project Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          <TextField
            label="Project Name" required fullWidth
            value={editForm.name || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            label="Project Code" fullWidth
            value={editForm.code || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
            inputProps={{ style: { fontFamily: 'monospace', fontWeight: 600 } }}
          />
          <Autocomplete
            options={clients}
            getOptionLabel={(option) => option.name}
            value={clients.find(c => c.id === editForm.clientId) || null}
            onChange={(_, val) => setEditForm(prev => ({ ...prev, clientId: val?.id || undefined }))}
            renderInput={(params) => <TextField {...params} label="Client" />}
          />
          <TextField
            label="Description" fullWidth multiline rows={3}
            value={editForm.description || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Project Color</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {PROJECT_COLORS.map(color => (
                <Box
                  key={color}
                  onClick={() => setEditForm(prev => ({ ...prev, color }))}
                  sx={{
                    width: 32, height: 32, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                    border: editForm.color === color ? '3px solid white' : '3px solid transparent',
                    boxShadow: editForm.color === color ? `0 0 0 2px ${color}` : 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': { transform: 'scale(1.15)' },
                  }}
                />
              ))}
            </Box>
          </Box>
          <FormControlLabel
            control={<Switch checked={editForm.isBillable ?? true} onChange={(e) => setEditForm(prev => ({ ...prev, isBillable: e.target.checked }))} />}
            label="Billable"
          />
          {editForm.isBillable && (
            <TextField
              label="Hourly Rate" type="number" fullWidth
              value={editForm.hourlyRate || ''}
              onChange={(e) => setEditForm(prev => ({ ...prev, hourlyRate: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          )}
          <TextField
            label="Budget Hours" type="number" fullWidth
            value={editForm.budgetHours || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, budgetHours: e.target.value }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editForm.status === 'archived'}
                onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.checked ? 'archived' : 'active' }))}
              />
            }
            label="Archived"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button color="error" onClick={() => { setEditOpen(false); setDeleteOpen(true); }}>
            Delete Project
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSubmit} disabled={!editForm.name?.trim() || editSubmitting}>
              {editSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          <TextField
            label="Task Name" required fullWidth autoFocus
            value={taskForm.name}
            onChange={(e) => setTaskForm(prev => ({ ...prev, name: e.target.value }))}
          />
          <FormControlLabel
            control={<Switch checked={taskForm.isBillable} onChange={(e) => setTaskForm(prev => ({ ...prev, isBillable: e.target.checked }))} />}
            label="Billable"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTaskSubmit} disabled={!taskForm.name.trim() || taskSubmitting}>
            {taskSubmitting ? 'Saving...' : editingTask ? 'Update Task' : 'Add Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{project.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteProject}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectDetailPage;
